import React, { useState, useEffect } from 'react';
import ExternalAccessApi from '../../../../api/external-access-api';
import PostmanUI from '../../../developer-access/components/PostmanUI';
import groupApiEndpointsData from '../../data/group-api-endpoints.json';
import './group-external-api-page.scss';
import router from '../../../../shared/top-level-declarations/history';
import Card from '../../../../shared/components/dashboard-layout/components/card/cardComponent';

const GroupExternalApiPage = () => {
  // API key state
  const [liveApiKey, setLiveApiKey] = useState(null);
  const [liveLoading, setLiveLoading] = useState(false);
  const [liveError, setLiveError] = useState(null);

  // Common state
  const [copied, setCopied] = useState(false);
  const [activeEndpoint, setActiveEndpoint] = useState('list-groups');
  const [endpoints] = useState(groupApiEndpointsData.endpoints);
  const [activeTab, setActiveTab] = useState('docs');

  const apiKey = liveApiKey;
  const loading = liveLoading;
  const error = liveError;

  useEffect(() => {
    fetchLiveApiKey();
  }, []);

  // Fetch Live API key
  const fetchLiveApiKey = async () => {
    try {
      setLiveLoading(true);
      setLiveError(null);
      const response = await ExternalAccessApi.private.getApiKey();
      if (response?.data?.data?.apiKey) {
        setLiveApiKey(response.data.data.apiKey);
      }
      setLiveLoading(false);
    } catch (err) {
      setLiveLoading(false);
      if (err.response && err.response.status === 404) {
        console.log('No live API key found for this organization');
      } else {
        setLiveError('Failed to fetch API key. Please try again.');
        console.error('Error fetching live API key:', err);
      }
    }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(apiKey).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const downloadApiEndpointsJson = () => {
    const dataStr = JSON.stringify(groupApiEndpointsData, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'group-external-api-endpoints.json';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (<>
    <div className="group-external-api-container">
        <div className="pageHeaderTitle">
          <div className="pageTitle">
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><g id="SVGRepo_bgCarrier" stroke-width="0"></g><g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round"></g><g id="SVGRepo_iconCarrier"> <path d="M7 8L3 11.6923L7 16M17 8L21 11.6923L17 16M14 4L10 20" stroke="var(--text-primary)" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"></path> </g></svg>
            Group External API
          </div>
        </div>

        <Card
          className={"api-key-card mb-4"}
          header={"API Keys"}
          subheader={"Use this API key to authenticate your requests to the Group External API."}
          notes={"This is your organization's live API key. Keep it secure and don't share it publicly."}
        >
          {loading ? (
              <div className="loading-indicator">Loading API key...</div>
            ) : (
              <>
                {apiKey ? (
                  <div className="api-key-display">
                    <div className="api-key-value">
                      <div className="codeBlock"><code>{apiKey}</code></div>
                      <button
                        className="customButton small"
                        onClick={copyToClipboard}
                        disabled={copied}
                      >
                        <span>{copied ? '✓ Copied!' : 'Copy Key'}</span>
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="no-api-key">
                    <p>
                      No API key found for your organization. You can{' '}
                      <button
                        className="btn btn-link p-0 align-baseline"
                        onClick={() => router.navigate('/developer-access')}
                      >
                        generate one on the Developer Access page
                      </button>
                      .
                    </p>
                  </div>
                )}
              </>
            )}
            {error && <div className="error-message">{error}</div>}
        </Card>

        <div className="docs-tabs">
          <ul className="nav nav-tabs" role="tablist">
            <li className="nav-item" role="presentation">
              <button
                className={`nav-link ${activeTab === 'docs' ? 'active' : ''}`}
                onClick={() => setActiveTab('docs')}
                type="button"
                role="tab"
              >
                API Documentation
              </button>
            </li>
            <li className="nav-item" role="presentation">
              <button
                className={`nav-link ${activeTab === 'playground' ? 'active' : ''}`}
                onClick={() => setActiveTab('playground')}
                type="button"
                role="tab"
              >
                Playground
              </button>
            </li>
          </ul>
        </div>

        {activeTab === 'docs' && (
        <Card
          className={"api-doc-card mb-4"}
          header={"API Documentation"}
          subheader={"Explore the available endpoints and learn how to integrate with the Group External API."}
          notes={""}
        >
          <div className="api-doc-actions">
            <button
              className="customButton small download-json-btn"
              onClick={downloadApiEndpointsJson}
            >
              <i className="mdi mdi-download"></i>
              <span>Download API Endpoints JSON</span>
            </button>
            <small className="text-muted">Download complete API documentation to share with AI copilots and development tools</small>
          </div>

          <div className="cardSubSection">
              <div className="cardSubSectionHeader">
                <h4>Base URL</h4>
                <div>All API endpoints are relative to the following base URL:</div>
                <div className="codeBlock">
                  <code>
                    {process.env.REACT_APP_API_BASE_URL.replace('api/v1', 'external/v1')}
                  </code>
                </div>
            </div>
          </div>

          <div className="cardSubSection">
              <div className="cardSubSectionHeader">
                <h4>Authentication</h4>
                <div>Include your API key in the request header</div>
                <div className="codeBlock">
                  <code>
                    x-api-key: YOUR_API_KEY
                  </code>
                </div>
            </div>
          </div>


          <div className="api-docs-container">
            <div className="api-sidebar">
              <h4>Available Endpoints</h4>
              <ul className="api-nav">
                {endpoints.map(endpoint => (
                  <li
                    key={endpoint.id}
                    className={activeEndpoint === endpoint.id ? 'active' : ''}
                    onClick={() => setActiveEndpoint(endpoint.id)}
                  >
                    <span className="endpoint-method">{endpoint.method}</span>
                    <span className="endpoint-path">{endpoint.path.split('/').slice(-1)[0]}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="api-content">
              {endpoints.map(endpoint => (
                activeEndpoint === endpoint.id && (
                  <div className="api-endpoint-details" key={endpoint.id}>
                    <div className="endpoint-header">
                      <span className={`endpoint-method ${endpoint.method.toLowerCase()}`}>{endpoint.method}</span>
                      <span className="endpoint-full-path">{endpoint.path}</span>
                    </div>
                    <div className="endpoint-description">
                      <p>{endpoint.description}</p>
                    </div>

                    <div className="endpoint-section">
                      <h5>Request</h5>
                      <pre className="codeBlock">
                        <code>
                          {`${endpoint.method} ${endpoint.path}

                          Headers:
                          ${Object.entries(endpoint.request.headers).map(([key, value]) => `${key}: ${value}`).join('\n')}`}
                                                      {endpoint.request.body && `

                          Body:
                          ${JSON.stringify(endpoint.request.body, null, 2)}`}
                        </code>
                      </pre>
                    </div>

                    <div className="endpoint-section">
                      <h5>Response</h5>
                      <pre className="codeBlock">
                        <code>
                          {JSON.stringify(endpoint.response.success.content, null, 2)}
                        </code>
                      </pre>
                    </div>

                    {endpoint.parameters && endpoint.parameters.length > 0 && (
                      <div className="endpoint-section">
                        <h5>Parameters</h5>
                        <table className="parameters">
                          <thead>
                            <tr>
                              <th>Name</th>
                              <th>Type</th>
                              <th>Required</th>
                              <th>Description</th>
                            </tr>
                          </thead>
                          <tbody>
                            {endpoint.parameters.map(param => (
                              <tr key={param.name}>
                                <td>{param.name}</td>
                                <td>{param.type}</td>
                                <td>{param.required ? 'Yes' : 'No'}</td>
                                <td>{param.description}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}

                    <div className="endpoint-section">
                      <h5>Status Codes</h5>
                      <table className="status-codes">
                        <tbody>
                          {endpoint.statusCodes.map(status => (
                            <tr key={status.code}>
                              <td>{status.code}</td>
                              <td>{status.description}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )
              ))}
            </div>
          </div>
        </Card>
        )}

        {activeTab === 'playground' && (
        <Card
          className={"mb-4"}
          header={"API Playground"}
          subheader={"Test your API endpoints directly from the browser with our interactive Postman-like interface."}
        >
          <PostmanUI
            endpoints={endpoints}
            apiKey={apiKey}
          />
        </Card>
        )}

    </div>
  </>);
};

export default GroupExternalApiPage;
